import { strToU8, zipSync } from 'fflate';

/**
 * Exportação em `.xlsx` de verdade — pasta do Excel, e não CSV com cara de pasta.
 *
 * O CSV de `csv.ts` resolve tabela que alguém vai somar e filtrar uma vez. Esta é para a
 * lista que vai circular: o Excel abre sem assistente, o acento chega certo em qualquer
 * idioma do Windows, o cabeçalho fica em negrito e congelado, e o filtro já vem ligado.
 *
 * É o caminho inverso de `@obraplan/table-reader/xlsx-reader`, com o mesmo argumento: um
 * `.xlsx` é um zip de XML, e o `fflate` já está no pacote. Uma biblioteca de planilha
 * custaria centenas de kB para escrever uma aba de texto.
 *
 * O texto vai **embutido** na célula (`inlineStr`), sem `sharedStrings.xml`: é um arquivo a
 * menos para manter coerente, e o Excel converte para compartilhado na primeira vez que
 * alguém salva.
 *
 * A aba que traz dinheiro e quantidade pode dar um formato numérico a cada coluna e pôr as
 * linhas de soma em negrito (`XlsxSheet.formats` e `boldRows`). Os números seguem números —
 * o formato só muda a aparência —, então o Excel ainda soma a coluna.
 */

export type XlsxCell = string | number;

export interface XlsxSheet {
  readonly name: string;
  readonly headers: readonly string[];
  readonly rows: readonly (readonly XlsxCell[])[];
  /** Largura de cada coluna, em caracteres. Ausente = calculada pelo conteúdo. */
  readonly widths?: readonly number[];
  /**
   * O formato numérico de cada coluna, no código do Excel: `#,##0.00`, `"R$" #,##0.0000`.
   * Ausente (ou `undefined` na posição) = geral.
   *
   * Só as células **numéricas** o levam, e continuam números: o formato muda como a célula
   * aparece, e não o que ela guarda — somar a coluna no Excel segue funcionando. O código é
   * o de qualquer idioma (separador de milhar com vírgula, decimal com ponto); é o Excel de
   * quem abre que o escreve na língua dele.
   */
  readonly formats?: readonly (string | undefined)[];
  /**
   * As linhas de `rows` em negrito, pela posição (de zero) — os subtotais e o fecho de uma
   * planilha que mistura soma e detalhe.
   */
  readonly boldRows?: readonly number[];
  /**
   * O filtro nos cabeçalhos. Ligado por padrão; desligado, a aba que tem texto solto abaixo da
   * tabela — as notas de um resumo — não oferece filtrar a nota.
   */
  readonly filter?: boolean;
  /**
   * Linhas acima do cabeçalho, em negrito — o título e o "Obra: …" de um formulário do cliente,
   * como o FORM.30. O cabeçalho desce para depois delas, e o congelamento e o filtro descem
   * junto. Não entram na largura das colunas: o título comprido não alarga a coluna A.
   */
  readonly preamble?: readonly (readonly XlsxCell[])[];
}

/** A linha (de um) do cabeçalho: depois do preâmbulo, se houver. */
const linhaDoCabecalho = (sheet: XlsxSheet): number => (sheet.preamble?.length ?? 0) + 1;

/**
 * O texto, pronto para dentro de um XML.
 *
 * Os caracteres de controle saem porque o Excel recusa a pasta inteira por um só deles —
 * "o arquivo está corrompido" —, e ninguém consegue saber qual célula foi.
 */
function xml(texto: string): string {
  return [...texto]
    .filter((letra) => {
      // Abaixo do espaço, o XML 1.0 só aceita tabulação e quebra de linha.
      const codigo = letra.charCodeAt(0);
      return codigo >= 32 || codigo === 9 || codigo === 10 || codigo === 13;
    })
    .join('')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** 0 → `A`, 25 → `Z`, 26 → `AA`. */
function coluna(indice: number): string {
  let resto = indice + 1;
  let letras = '';
  while (resto > 0) {
    const digito = (resto - 1) % 26;
    letras = String.fromCharCode(65 + digito) + letras;
    resto = Math.floor((resto - 1) / 26);
  }
  return letras;
}

/**
 * O nome da aba como o Excel aceita: até 31 caracteres e sem `[]:*?/\`.
 *
 * Fora disso o Excel não reclama do nome — reclama do arquivo, e oferece "recuperar".
 */
function nomeDaAba(nome: string): string {
  const limpo = nome
    .replace(/[[\]:*?/\\]/g, ' ')
    .trim()
    .slice(0, 31);
  return limpo === '' ? 'Planilha 1' : limpo;
}

/**
 * Os nomes das abas, sem repetir.
 *
 * O Excel compara sem diferenciar caixa e recusa a pasta com dois nomes iguais — de novo o
 * "arquivo corrompido" sem dizer qual aba. Quem repete ganha " (2)", " (3)", e o nome continua
 * dentro dos 31 caracteres.
 */
function nomesDasAbas(sheets: readonly XlsxSheet[]): string[] {
  const usados = new Set<string>();
  return sheets.map((sheet) => {
    const base = nomeDaAba(sheet.name);
    let nome = base;
    for (let n = 2; usados.has(nome.toLocaleLowerCase('pt-BR')); n += 1) {
      const sufixo = ` (${String(n)})`;
      nome = `${base.slice(0, 31 - sufixo.length)}${sufixo}`;
    }
    usados.add(nome.toLocaleLowerCase('pt-BR'));
    return nome;
  });
}

/**
 * Estilo 0 é o padrão, e o 1 é o negrito — o do cabeçalho e o das linhas de soma. O resto
 * nasce sob demanda, um por combinação de negrito e formato numérico que as abas pedem.
 */
const ESTILO_PADRAO = 0;
const ESTILO_NEGRITO = 1;

/** Os formatos personalizados do Excel começam no 164; abaixo disso são os de fábrica. */
const PRIMEIRO_FORMATO_PROPRIO = 164;

/**
 * O registro dos estilos da pasta.
 *
 * Os estilos são da pasta, e não da aba: um `<xf>` em `styles.xml` serve a todas. Por isso as
 * abas pedem o estilo aqui enquanto são escritas, e o `styles.xml` só sai depois da última —
 * com a pasta que não pede formato nem negrito além do cabeçalho, igual ao de sempre.
 */
interface Estilos {
  /** O índice do estilo (`s="…"` da célula) para essa combinação. */
  indice(negrito: boolean, formato?: string): number;
  /** O `styles.xml`, com o que foi pedido até agora. */
  xml(): string;
}

function criarEstilos(): Estilos {
  const formatos = new Map<string, number>();
  const combinacoes = new Map<string, number>();
  // O 0 e o 1 já existem, sem formato: o padrão e o negrito.
  const xfs: string[] = [
    '<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>',
    '<xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/>',
  ];

  return {
    indice(negrito, formato) {
      if (formato === undefined || formato === '') {
        return negrito ? ESTILO_NEGRITO : ESTILO_PADRAO;
      }
      const chave = `${negrito ? 'b' : 'n'}|${formato}`;
      const existente = combinacoes.get(chave);
      if (existente !== undefined) return existente;

      let formatoId = formatos.get(formato);
      if (formatoId === undefined) {
        formatoId = PRIMEIRO_FORMATO_PROPRIO + formatos.size;
        formatos.set(formato, formatoId);
      }
      const novo = xfs.length;
      xfs.push(
        `<xf numFmtId="${String(formatoId)}" fontId="${negrito ? '1' : '0'}" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"${negrito ? ' applyFont="1"' : ''}/>`,
      );
      combinacoes.set(chave, novo);
      return novo;
    },

    xml() {
      // `numFmts` vem antes de `fonts`: o esquema do Excel fixa a ordem, e fora dela a pasta
      // abre com "recuperar".
      const numFmts =
        formatos.size === 0
          ? ''
          : `<numFmts count="${String(formatos.size)}">${[...formatos]
              .map(
                ([codigo, id]) => `<numFmt numFmtId="${String(id)}" formatCode="${xml(codigo)}"/>`,
              )
              .join('')}</numFmts>`;
      return (
        '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
        '<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">' +
        numFmts +
        '<fonts count="2"><font><sz val="11"/><name val="Calibri"/></font>' +
        '<font><b/><sz val="11"/><name val="Calibri"/></font></fonts>' +
        '<fills count="2"><fill><patternFill patternType="none"/></fill>' +
        '<fill><patternFill patternType="gray125"/></fill></fills>' +
        '<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>' +
        '<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>' +
        `<cellXfs count="${String(xfs.length)}">${xfs.join('')}</cellXfs>` +
        '<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>' +
        '</styleSheet>'
      );
    },
  };
}

function celula(valor: XlsxCell, referencia: string, estilo?: number): string {
  const s = estilo === undefined ? '' : ` s="${String(estilo)}"`;
  if (typeof valor === 'number' && Number.isFinite(valor)) {
    return `<c r="${referencia}"${s}><v>${String(valor)}</v></c>`;
  }
  const texto = String(valor);
  if (texto === '') return '';
  // `xml:space` segura o espaço das pontas, que o XML descartaria sem avisar.
  return `<c r="${referencia}"${s} t="inlineStr"><is><t xml:space="preserve">${xml(texto)}</t></is></c>`;
}

/** O estilo de cada célula da linha; `undefined` = o padrão, que a célula nem declara. */
type EstiloDaCelula = (valor: XlsxCell, indice: number) => number | undefined;

function linha(numero: number, valores: readonly XlsxCell[], estiloDe: EstiloDaCelula): string {
  const celulas = valores
    .map((valor, indice) =>
      celula(valor, `${coluna(indice)}${String(numero)}`, estiloDe(valor, indice)),
    )
    .join('');
  return `<row r="${String(numero)}">${celulas}</row>`;
}

/**
 * A largura que cabe o texto mais comprido da coluna, com um teto.
 *
 * Sem teto, um nome de serviço de 80 caracteres empurra as outras colunas para fora da
 * tela; com ele, o texto longo só passa por baixo da coluna vizinha, como no Excel.
 */
function larguras(sheet: XlsxSheet): number[] {
  return sheet.headers.map((cabecalho, indice) => {
    const declarada = sheet.widths?.[indice];
    if (declarada !== undefined) return declarada;
    const maior = sheet.rows.reduce(
      (atual, valores) => Math.max(atual, String(valores[indice] ?? '').length),
      cabecalho.length,
    );
    return Math.min(Math.max(maior + 2, 8), 60);
  });
}

/** O filtro está ligado? Está, a menos que a aba peça o contrário. */
const comFiltro = (sheet: XlsxSheet): boolean => sheet.filter !== false;

/**
 * O estilo de uma célula do corpo: o formato da coluna, só se a célula é número, e o negrito
 * da linha. A coluna que mistura rótulo e valor — a de uma planilha com linha de soma — leva o
 * formato só nos valores; o rótulo fica como texto.
 */
function estiloDoCorpo(sheet: XlsxSheet, estilos: Estilos, negrito: boolean): EstiloDaCelula {
  return (valor, indice) => {
    const formato =
      typeof valor === 'number' && Number.isFinite(valor) ? sheet.formats?.[indice] : undefined;
    const estilo = estilos.indice(negrito, formato);
    return estilo === ESTILO_PADRAO ? undefined : estilo;
  };
}

function planilha(sheet: XlsxSheet, estilos: Estilos): string {
  const ultimaColuna = coluna(Math.max(sheet.headers.length - 1, 0));
  const cabecalho = linhaDoCabecalho(sheet);
  const ultimaLinha = sheet.rows.length + cabecalho;
  const cols = larguras(sheet)
    .map(
      (largura, indice) =>
        `<col min="${String(indice + 1)}" max="${String(indice + 1)}" width="${String(largura)}" customWidth="1"/>`,
    )
    .join('');

  const negritos = new Set(sheet.boldRows);
  const corpo = [
    ...(sheet.preamble ?? []).map((valores, indice) =>
      linha(indice + 1, valores, () => ESTILO_NEGRITO),
    ),
    linha(cabecalho, sheet.headers, () => ESTILO_NEGRITO),
    ...sheet.rows.map((valores, indice) =>
      linha(indice + cabecalho + 1, valores, estiloDoCorpo(sheet, estilos, negritos.has(indice))),
    ),
  ].join('');

  return (
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
    '<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">' +
    // O cabeçalho congelado: rolar cento e cinquenta serviços sem perder de vista qual
    // coluna é a unidade.
    '<sheetViews><sheetView workbookViewId="0">' +
    `<pane ySplit="${String(cabecalho)}" topLeftCell="A${String(cabecalho + 1)}" activePane="bottomLeft" state="frozen"/>` +
    '</sheetView></sheetViews>' +
    `<cols>${cols}</cols>` +
    `<sheetData>${corpo}</sheetData>` +
    (comFiltro(sheet)
      ? `<autoFilter ref="A${String(cabecalho)}:${ultimaColuna}${String(ultimaLinha)}"/>`
      : '') +
    '</worksheet>'
  );
}

function contentTypes(abas: number): string {
  return (
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
    '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">' +
    '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>' +
    '<Default Extension="xml" ContentType="application/xml"/>' +
    '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>' +
    Array.from(
      { length: abas },
      (_, indice) =>
        `<Override PartName="/xl/worksheets/sheet${String(indice + 1)}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`,
    ).join('') +
    '<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>' +
    '</Types>'
  );
}

const RELS =
  '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
  '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
  '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>' +
  '</Relationships>';

/** Uma relação por aba, na ordem; a das folhas de estilo vem depois da última. */
function workbookRels(abas: number): string {
  return (
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
    '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
    Array.from(
      { length: abas },
      (_, indice) =>
        `<Relationship Id="rId${String(indice + 1)}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${String(indice + 1)}.xml"/>`,
    ).join('') +
    `<Relationship Id="rId${String(abas + 1)}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>` +
    '</Relationships>'
  );
}

function workbook(sheets: readonly XlsxSheet[], nomes: readonly string[]): string {
  const abas = sheets
    .map(
      (_, indice) =>
        `<sheet name="${xml(nomes[indice]!)}" sheetId="${String(indice + 1)}" r:id="rId${String(indice + 1)}"/>`,
    )
    .join('');
  // O Excel só liga o filtro do `autoFilter` se o intervalo também tiver este nome — e um por
  // aba, cada um preso à própria (`localSheetId` é a posição da aba, contada de zero).
  const filtros = sheets
    .map((sheet, indice) => {
      if (!comFiltro(sheet)) return '';
      const ultimaColuna = coluna(Math.max(sheet.headers.length - 1, 0));
      const nome = xml(nomes[indice]!).replace(/'/g, "''");
      const cabecalho = linhaDoCabecalho(sheet);
      return `<definedName name="_xlnm._FilterDatabase" localSheetId="${String(indice)}" hidden="1">'${nome}'!$A$${String(cabecalho)}:$${ultimaColuna}$${String(sheet.rows.length + cabecalho)}</definedName>`;
    })
    .join('');
  return (
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
    '<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" ' +
    'xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">' +
    `<sheets>${abas}</sheets>` +
    // `definedNames` vazio o Excel recusa: sem nenhum filtro na pasta, o elemento nem sai.
    (filtros === '' ? '' : `<definedNames>${filtros}</definedNames>`) +
    '</workbook>'
  );
}

/**
 * A pasta em bytes — o que vai para o `Blob` ou para o disco.
 *
 * Uma aba ou várias: a lista que circula costuma ter uma, e o modelo de índices tem duas. O
 * que passa sem aba nenhuma sai com uma aba vazia, porque pasta sem aba o Excel não abre.
 */
export function toXlsx(sheets: XlsxSheet | readonly XlsxSheet[]): Uint8Array {
  const dadas: readonly XlsxSheet[] = 'headers' in sheets ? [sheets] : sheets;
  const abas: readonly XlsxSheet[] =
    dadas.length > 0 ? dadas : [{ name: 'Planilha 1', headers: [], rows: [] }];
  const nomes = nomesDasAbas(abas);
  // As abas primeiro: é escrevendo-as que se descobre quais estilos a pasta precisa.
  const estilos = criarEstilos();
  const folhas = abas.map((sheet) => planilha(sheet, estilos));

  return zipSync({
    '[Content_Types].xml': strToU8(contentTypes(abas.length)),
    '_rels/.rels': strToU8(RELS),
    'xl/workbook.xml': strToU8(workbook(abas, nomes)),
    'xl/_rels/workbook.xml.rels': strToU8(workbookRels(abas.length)),
    'xl/styles.xml': strToU8(estilos.xml()),
    ...Object.fromEntries(
      folhas.map((folha, indice) => [
        `xl/worksheets/sheet${String(indice + 1)}.xml`,
        strToU8(folha),
      ]),
    ),
  });
}

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/** Entrega a pasta ao navegador e revoga a URL temporária logo em seguida. */
export function downloadXlsx(fileName: string, bytes: Uint8Array): void {
  const url = URL.createObjectURL(new Blob([bytes as unknown as BlobPart], { type: XLSX_MIME }));
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName.endsWith('.xlsx') ? fileName : `${fileName}.xlsx`;
  link.click();
  URL.revokeObjectURL(url);
}
